import { formatAddress, formatDate, formatPhone, titleCase } from '../BusinessDetailReport/businessReportModel'
import { moreFromLexis } from './moreFromLexis'

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const text = (value) => (value == null || typeof value === 'object' ? '' : String(value)).trim()
const upper = (value) => text(value).toUpperCase()
const dateValue = (date) => (date && typeof date === 'object' && date.Year ? new Date(date.Year, (date.Month || 1) - 1, date.Day || 1).getTime() : 0)
const yes = (value) => value === true || /^(Y|YES|TRUE|1)$/.test(upper(value))
const MEDICAL = /MEDIC|NURS|PHARMAC|DENTAL|DENTIST|OSTEOPATH|CHIROPRACT|PODIATR|OPTOMETR|PHYSICIAN|\bMD\b|\bRN\b|LPN|APRN|CONTROLLED|NARCOTIC|THERAPIST|PSYCHOLOG/i

export const licenseStatus = (status, expiration, now) => {
    const value = upper(status)
    const disciplinary = value.match(/REVOK|SUSPEN|SURRENDER|PROBATION|DISCIPLIN|(?<!UN)RESTRICT|DENIED|EMERGENCY/)
    if (disciplinary) return { label: titleCase(value.replace(/[[\]_]/g, ' ').trim()) || 'Disciplinary action', kind: 'disciplinary' }
    const validUntil = value.match(/VALID UNTIL (\d{2})\/(\d{2})\/(\d{4})/)
    const pastValidUntil = validUntil && new Date(Number(validUntil[3]), Number(validUntil[1]) - 1, Number(validUntil[2])).getTime() < now.getTime()
    if (/EXPIRED|LAPSED|NOT RENEWED|DELINQUENT|^E$|INACTIVE|^I$|CLOSED|CANCEL|NULL AND VOID|DERIVED_INACTIVE/.test(value) || pastValidUntil
        || (!value && dateValue(expiration) && dateValue(expiration) < now.getTime())) return { label: 'Expired or inactive', kind: 'expired' }
    const words = value.replace(/[_[\]]/g, ' ')
    if (!/\b(NOT|NON|UN)\b/.test(words) && /\b(ACTIVE|CURRENT|CLEAR|LICENSED|PERMANENT|VALID)\b|^A$|^AC$/.test(words)) return { label: 'Active', kind: 'active' }
    return { label: value ? titleCase(value) : 'Status not given', kind: 'other' }
}

const action = (l) => {
    const a = l?.Action || {}
    return {
        what: [titleCase(a.Type), text(a.Description)].filter(Boolean).join(': '),
        when: formatDate(a.EffectiveDate),
        violation: [text(a.ViolationDescription), a.ViolationDate && `on ${formatDate(a.ViolationDate)}`].filter(Boolean).join(' '),
    }
}

export function buildLicenses(licenses, now = new Date()) {
    const rows = list(licenses).filter(l => l && typeof l === 'object').map(l => {
        const status = licenseStatus(l.Status, l.ExpirationDate, now)
        const boardAction = yes(l.BoardActionIndicator)
        const a = action(l)
        const state = text(l.SourceState || l.Address?.State)
        return {
            more: moreFromLexis(l, 'ProfessionalLicenses/ProfessionalLicense[]'),
            key: `${upper(l.LicenseType)}|${upper(state)}`,
            number: upper(l.LicenseNumber).replace(/[^A-Z0-9]/g, ''), renewedAt: l.LastRenewalDate, expiresAt: l.ExpirationDate,
            status, boardAction, medical: MEDICAL.test(`${l.LicenseType || ''} ${l.ProfessionOrBoard || ''}`),
            sortKey: Math.max(dateValue(l.StatusEffectiveDate), dateValue(l.LastRenewalDate), dateValue(l.IssuedDate))
                || dateValue(l.ExpirationDate) || dateValue(l.DateLastSeen),
            title: [titleCase(l.LicenseType) || 'License', state].filter(Boolean).join(' · '),
            facts: [titleCase(l.ProfessionOrBoard), l.ExpirationDate && `Expires ${formatDate(l.ExpirationDate)}`, l.LicenseNumber && `No. ${text(l.LicenseNumber)}`]
                .filter(Boolean).join(' · '),
            details: [
                ['Status', [titleCase(text(l.Status).replace(/[[\]_]/g, ' ').trim()), l.StatusEffectiveDate && `since ${formatDate(l.StatusEffectiveDate)}`].filter(Boolean).join(' ')],
                ['Board action', boardAction ? [a.what || 'Yes', a.when && `(${a.when})`].filter(Boolean).join(' ') : ''],
                ['Violation', a.violation],
                ['Sanction number', text(l.SanctionNumber || l.Action?.CaseNumber)],
                ['License number', text(l.LicenseNumber)],
                ['Provider number', text(l.ProviderNumber)],
                ['Board', titleCase(l.ProfessionOrBoard)],
                ['Issued', formatDate(l.IssuedDate)],
                ['Expires', formatDate(l.ExpirationDate)],
                ['Last renewal', formatDate(l.LastRenewalDate)],
                ['Practice', [titleCase(l.PracticeType), titleCase(l.Occupation)].filter(Boolean).join(' · ')],
                ['Company', titleCase(l.CompanyName)],
                ['Education', [titleCase(l.Education1?.School), titleCase(l.Education1?.Degree), text(l.Education1?.DatesAttended)].filter(Boolean).join(' · ')],
                ['Other details', titleCase(l.AdditionalLicensingSpecs)],
                ['Last seen', formatDate(l.DateLastSeen)],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        }
    })
    rows.forEach(r => {
        r.renewed = r.status.kind === 'expired' && Boolean(r.number) && rows.some(o => o !== r && o.key === r.key && o.number === r.number
            && o.status.kind === 'active' && dateValue(o.renewedAt) > Math.max(dateValue(r.renewedAt), dateValue(r.expiresAt)))
        r.chips = [
            r.status.kind === 'disciplinary' ? { label: r.status.label, tone: 'declined' }
                : r.status.kind === 'expired' ? { label: r.renewed ? 'Renewed later' : 'Expired or inactive', tone: 'neutral' }
                    : { label: r.status.label, tone: 'neutral' },
            r.boardAction && { label: 'Board action', tone: 'declined' },
        ].filter(Boolean)
    })
    rows.sort((a, b) => (b.status.kind === 'disciplinary' || b.boardAction) - (a.status.kind === 'disciplinary' || a.boardAction)
        || b.medical - a.medical || b.sortKey - a.sortKey)
    return {
        rows,
        summary: {
            count: rows.length,
            active: rows.filter(r => r.status.kind === 'active').length,
            disciplinary: rows.filter(r => r.status.kind === 'disciplinary' || r.boardAction).length,
            medical: rows.filter(r => r.medical).length,
        },
    }
}

export function buildDeaRegistrations(registrations, now = new Date()) {
    const rows = list(registrations).filter(Boolean).map(r => {
        const expired = dateValue(r.ExpirationDate) && dateValue(r.ExpirationDate) < now.getTime()
        return {
            more: moreFromLexis(r, 'ControlledSubstances/ControlledSubstance[]/ControlledSubstancesInfo/ControlledSubstanceInfo[]'),
            title: [titleCase(r.BusinessType) || 'DEA registration', titleCase(r.CompanyName)].filter(Boolean).join(' · '),
            facts: [r.RegistrationNumber && `No. ${text(r.RegistrationNumber)}`, r.ExpirationDate && `${expired ? 'Expired' : 'Expires'} ${formatDate(r.ExpirationDate)}`,
                r.Address?.State].filter(Boolean).join(' · '),
            chips: [expired ? { label: 'Expired', tone: 'watch' } : r.ExpirationDate ? { label: 'Active', tone: 'neutral' } : null].filter(Boolean),
            details: [
                ['Drug schedules', text(r.DrugSchedules).split(',').map(s => s.trim()).filter(Boolean).join(', ')],
                ['Registration number', text(r.RegistrationNumber)],
                ['Business type', titleCase(r.BusinessType)],
                ['Company', titleCase(r.CompanyName)],
                ['Where', [titleCase(r.Address?.City), r.Address?.State].filter(Boolean).join(', ')],
                ['Expires', formatDate(r.ExpirationDate)],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
            expired,
        }
    })
    return { rows, summary: { count: rows.length, expired: rows.filter(r => r.expired).length } }
}

const FFL_TYPES = {
    '01': 'Firearms dealer or gunsmith', '02': 'Pawnbroker in firearms', '03': 'Collector of curios and relics', '06': 'Ammunition manufacturer',
    '07': 'Firearms manufacturer', '08': 'Firearms importer', '09': 'Dealer in destructive devices', '10': 'Manufacturer of destructive devices',
    '11': 'Importer of destructive devices',
}

export function buildFirearmLicenses(person, now = new Date()) {
    const value = person?.FirearmExplosives && typeof person.FirearmExplosives === 'object' ? Object.values(person.FirearmExplosives).find(Array.isArray) : null
    const rows = (value || []).filter(r => r && typeof r === 'object').map(f => {
        const type = text(f.LicenseType)
        const typeLabel = FFL_TYPES[type.padStart(2, '0')] || (type ? `License type ${type}` : '')
        const expired = dateValue(f.LicenseExpireDate) && dateValue(f.LicenseExpireDate) < now.getTime()
        const names = list(f.LicenseNames?.Name).map(n => titleCase([n?.First, n?.Middle, n?.Last].filter(Boolean).join(' ') || n?.Full)).filter(Boolean)
        return {
            more: moreFromLexis(f, 'FirearmExplosives/FirearmExplosive[]'),
            expired,
            title: titleCase(f.TradeName || f.LicenseCompany) || 'Firearms or explosives license',
            facts: [typeLabel, f.LicenseIssueState, f.LicenseExpireDate && `${expired ? 'Expired' : 'Expires'} ${formatDate(f.LicenseExpireDate)}`].filter(Boolean).join(' · '),
            chips: [{ label: expired ? 'Expired' : 'Active', tone: 'neutral' }],
            details: [
                ['License number', text(f.LicenseNumber)],
                ['Type', [typeLabel, type && `(code ${type})`].filter(Boolean).join(' ')],
                ['Business', [titleCase(f.LicenseCompany), f.TradeName && f.TradeName !== f.LicenseCompany && `trading as ${titleCase(f.TradeName)}`].filter(Boolean).join(', ')],
                ['Names on the license', names.join('; ')],
                ['Premises', formatAddress(f.PremiseAddress)],
                ['Mailing address', formatAddress(f.MailingAddress) !== formatAddress(f.PremiseAddress) ? formatAddress(f.MailingAddress) : ''],
                ['Business phone', formatPhone(f.BusinessPhone)],
                ['County', titleCase(f.CountyName)],
                ['ATF region', text(f.LicenseRegion)],
                ['Expires', formatDate(f.LicenseExpireDate)],
            ].filter(([, v]) => v).map(([label, v]) => ({ label, value: v })),
        }
    })
    return { rows, summary: { count: rows.length, expired: rows.filter(r => r.expired).length } }
}
